import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import CheckInModal from '../components/CheckInModal';
import NavBar from '../components/NavBar';
import DailyQuote from '../components/DailyQuote';
import NewsCard from '../components/NewsCard';

const Dashboard = ({ user, onLogout }) => {
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [redAlertStatus, setRedAlertStatus] = useState(null);
  const [selectedHours, setSelectedHours] = useState(3);
  const [alertLoading, setAlertLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const [statusRes, alertRes] = await Promise.all([
        axios.get(`${API}/checkin/status`),
        axios.get(`${API}/checkin/soft-alert/status`)
      ]);
      setStatus(statusRes.data);
      setRedAlertStatus(alertRes.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const handleCheckIn = async (formData) => {
    setChecking(true);
    try {
      const response = await axios.post(`${API}/checkin`, formData);
      toast.success(response.data.message);
      await fetchStatus();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to perform check-in');
    } finally {
      setChecking(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="dashboard-container" data-testid="dashboard-page">
      <NavBar onLogout={onLogout} />

      <div className="dashboard-content">
        <DailyQuote />
        <NewsCard />
        <div className="welcome-section" data-testid="welcome-section">
          <h1 className="welcome-title" data-testid="welcome-title">Hello, {user.name}!</h1>
          
          {loading ? (
            <p>Loading status...</p>
          ) : (
            <>
              {status?.is_snoozed ? (
                <p className="status-ok" data-testid="status-snoozed">
                  😴 Check-in snoozed for 30 minutes
                </p>
              ) : status?.overdue ? (
                <div>
                  <p className="status-overdue" data-testid="status-overdue">
                    ⚠️ Your check-in is overdue! Please check in now.
                  </p>
                  <button 
                    className="snooze-button"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/checkin/snooze`);
                        toast.success('Check-in snoozed for 30 minutes');
                        fetchStatus();
                      } catch (error) {
                        toast.error('Failed to snooze');
                      }
                    }}
                    style={{
                      padding: '0.75rem 2rem',
                      background: '#ffc107',
                      color: '#000',
                      border: 'none',
                      borderRadius: '50px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginTop: '1rem'
                    }}
                    data-testid="snooze-button"
                  >
                    😴 Snooze 30 Minutes
                  </button>
                </div>
              ) : (
                <p className="status-ok" data-testid="status-ok">
                  ✓ You're all set! Next check-in in {status?.hours_until_due} hours.
                </p>
              )}

              <button 
                className="checkin-button" 
                onClick={() => setShowModal(true)}
                disabled={checking}
                data-testid="checkin-button"
              >
                I'm OK ✓
              </button>

              {/* Quick Alert Buttons */}
              <div style={{ 
                marginTop: '2rem', 
                padding: '1.5rem', 
                background: '#f8f9fa', 
                borderRadius: '12px',
                border: '1px solid #e8f4f8'
              }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#2c5364', fontSize: '1.1rem', textAlign: 'center' }}>
                  Not Feeling Your Best?
                </h3>
                
                {/* Active Red Alert Warning */}
                {redAlertStatus?.red_alert_active && (
                  <div style={{
                    background: '#f8d7da',
                    border: '2px solid #f5c6cb',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, color: '#721c24', fontWeight: '600' }}>
                      ⏰ Check-in Required! 
                      {redAlertStatus.hours_remaining > 0 
                        ? ` ${redAlertStatus.hours_remaining.toFixed(1)} hours remaining`
                        : ' OVERDUE - Escalation may begin soon!'
                      }
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/checkin/soft-alert/clear`);
                          toast.success('Alert cleared! Glad you are okay.');
                          fetchStatus();
                        } catch (error) {
                          toast.error('Failed to clear alert');
                        }
                      }}
                      style={{
                        marginTop: '0.75rem',
                        padding: '0.5rem 1.5rem',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '25px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ✓ I'm Okay Now - Clear Alert
                    </button>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {/* Yellow Button - Not great but okay */}
                  <button
                    onClick={async () => {
                      setAlertLoading(true);
                      try {
                        const response = await axios.post(`${API}/checkin/soft-alert`, {
                          alert_type: 'yellow',
                          notes: 'Not feeling great but okay'
                        });
                        toast.success(response.data.message);
                        fetchStatus();
                      } catch (error) {
                        toast.error('Failed to send alert');
                      } finally {
                        setAlertLoading(false);
                      }
                    }}
                    disabled={alertLoading}
                    style={{
                      padding: '1rem 1.5rem',
                      background: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
                      color: '#000',
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: '600',
                      cursor: alertLoading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      minWidth: '200px',
                      boxShadow: '0 4px 15px rgba(255, 193, 7, 0.3)',
                      opacity: alertLoading ? 0.7 : 1
                    }}
                    data-testid="yellow-alert-button"
                  >
                    🟡 Not Great, But I'm OK
                    <div style={{ fontSize: '0.8rem', fontWeight: 'normal', marginTop: '0.25rem' }}>
                      No escalation needed
                    </div>
                  </button>

                  {/* Red Button - Check on me */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={async () => {
                        setAlertLoading(true);
                        try {
                          const response = await axios.post(`${API}/checkin/soft-alert`, {
                            alert_type: 'red',
                            hours_until_checkin: selectedHours,
                            notes: `Requested check-in in ${selectedHours} hours`
                          });
                          toast.success(response.data.message);
                          fetchStatus();
                        } catch (error) {
                          toast.error('Failed to send alert');
                        } finally {
                          setAlertLoading(false);
                        }
                      }}
                      disabled={alertLoading || redAlertStatus?.red_alert_active}
                      style={{
                        padding: '1rem 1.5rem',
                        background: redAlertStatus?.red_alert_active 
                          ? '#6c757d' 
                          : 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontWeight: '600',
                        cursor: (alertLoading || redAlertStatus?.red_alert_active) ? 'not-allowed' : 'pointer',
                        fontSize: '1rem',
                        minWidth: '200px',
                        boxShadow: redAlertStatus?.red_alert_active ? 'none' : '0 4px 15px rgba(220, 53, 69, 0.3)',
                        opacity: (alertLoading || redAlertStatus?.red_alert_active) ? 0.7 : 1
                      }}
                      data-testid="red-alert-button"
                    >
                      🔴 Check On Me
                      <div style={{ fontSize: '0.8rem', fontWeight: 'normal', marginTop: '0.25rem' }}>
                        {redAlertStatus?.red_alert_active 
                          ? 'Alert already active' 
                          : `Escalate if no check-in in ${selectedHours}hrs`
                        }
                      </div>
                    </button>
                    
                    {/* Hours Dropdown */}
                    {!redAlertStatus?.red_alert_active && (
                      <select
                        value={selectedHours}
                        onChange={(e) => setSelectedHours(parseInt(e.target.value))}
                        style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '8px',
                          border: '2px solid #dc3545',
                          background: 'white',
                          color: '#dc3545',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                        data-testid="hours-select"
                      >
                        <option value={3}>Check in 3 hours</option>
                        <option value={4}>Check in 4 hours</option>
                        <option value={5}>Check in 5 hours</option>
                        <option value={6}>Check in 6 hours</option>
                        <option value={7}>Check in 7 hours</option>
                        <option value={8}>Check in 8 hours</option>
                      </select>
                    )}
                  </div>
                </div>

                <p style={{ 
                  margin: '1rem 0 0', 
                  color: '#6b8899', 
                  fontSize: '0.85rem', 
                  textAlign: 'center' 
                }}>
                  🟡 Yellow: Lets us know you're not 100% but okay<br/>
                  🔴 Red: Sets a mandatory check-in timer - escalation starts if missed
                </p>
              </div>

              <div className="next-checkin-info" data-testid="next-checkin-info">
                <strong>Next Check-in Due:</strong><br />
                {status?.next_checkin_due ? formatDate(status.next_checkin_due) : 'Loading...'}
              </div>
            </>
          )}
        </div>
      </div>

      {showModal && (
        <CheckInModal
          onClose={() => setShowModal(false)}
          onSubmit={handleCheckIn}
          loading={checking}
        />
      )}
    </div>
  );
};

export default Dashboard;